import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import * as JSZipNS from 'jszip';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from 'src/app/service/admin.service';
import { EventsService } from 'src/app/service/events.service';
import { SubmissionStatus } from 'src/app/enum/submission.status';
import { CertificatesService } from 'src/app/service/certificates.service';
import { CertificatePdfService } from 'src/app/service/certificate-pdf.service';
import { NotificationService } from 'src/app/service/notification.service';
import { Event } from 'src/app/model/event';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';
import { SelectOption } from 'src/app/model/select.option';

// Interop CJS/ESM robusto (mesmo do xlsx-export): jszip usa `export =`.
const JSZip: any = (JSZipNS as any).default || JSZipNS;
import {
  CERTIFICATE_SHEET_H, CERTIFICATE_SHEET_W,
  CERTIFICATE_VAR_NAMES,
  CertificateElement, CertificateEmissionRules, CertificateImage, CertificateImageElement, CertificateTemplate,
  CertificateTextElement, CertificateVars,
  isImageElement, isTextElement,
  emptyCertificateTemplate, newCertificateElementId, sanitizeCertificateTemplate,
  serializeCertificateTemplate,
} from 'src/app/model/certificate-template';

/**
 * Editor do MODELO DE CERTIFICADO de apresentação do evento (chair).
 *
 * Superfície de edição = a MESMA folha A4 do certificado do autor
 * (<app-certificate-sheet editable>): arrastar/redimensionar acontecem lá;
 * este componente cuida do painel lateral (texto, crop, fundo, biblioteca de
 * imagens), do dirty-tracking e do exportar/importar (.json com imagens
 * embutidas em base64 — reuso entre eventos/instalações).
 *
 * O template é um objeto MUTÁVEL compartilhado com a folha; toda edição marca
 * `dirty` e o salvamento persiste o JSON serializado (sem urls voláteis).
 */
@Component({
  selector: 'app-certificate-template-editor',
  templateUrl: './certificate-template-editor.component.html',
  styleUrls: ['./certificate-template-editor.component.scss'],
})
export class CertificateTemplateEditorComponent implements OnInit, AfterViewInit, OnDestroy, HasUnsavedChanges {
  readonly icons = EVENT_ADMIN_ICONS;
  readonly sheetW = CERTIFICATE_SHEET_W;
  readonly sheetH = CERTIFICATE_SHEET_H;

  event: Event;
  loading = true;
  saving = false;
  dirty = false;

  template: CertificateTemplate;
  images: CertificateImage[] = [];
  /** Regras que sobreviveram à exclusão do modelo — revivem no "Criar do zero". */
  private dormantEmission: CertificateEmissionRules | null = null;

  /** Aba ativa do painel: propriedades (edição) × regras de emissão. */
  panelTab: 'props' | 'emission' = 'props';

  /** Status oferecidos nas regras (DELETED fora — submissão deletada não emite nada). */
  readonly statusOptions = SubmissionStatus.toSelectable().filter(o => String(o.id) !== 'DELETED');

  // Seleção com SETTER: qualquer troca (clique na folha, adicionar caixa,
  // load/import/remover) re-sincroniza os controles reactive do painel.
  // Selecionar um elemento estando em "Regras de emissão" troca pra
  // "Propriedades" (selecionar é intenção inequívoca de edição); o caminho
  // inverso NÃO desseleciona — voltar reencontra o elemento.
  private _selectedId: string | null = null;
  get selectedId(): string | null { return this._selectedId; }
  set selectedId(v: string | null) {
    this._selectedId = v;
    if (v) { this.panelTab = 'props'; }
    this.syncPanelForm();
  }

  /** Marca/desmarca um status. Lista vazia É estado válido: NINGUÉM emite. */
  toggleEmissionStatus(status: string): void {
    if (!this.template) { return; }
    const current = (this.template.emission && this.template.emission.allowedStatuses) || [];
    const next = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status];
    this.template.emission = { allowedStatuses: next };
    this.markDirty();
  }

  isEmissionStatusChecked(status: string): boolean {
    return !!(this.template && this.template.emission
      && this.template.emission.allowedStatuses.includes(status));
  }

  /**
   * Ponte reactive p/ os controles JEMS3 do painel (app-jems-select é
   * FormGroup-based; o resto do painel edita o template direto via ngModel).
   * valueChanges → template; seleção → setValue({emitEvent:false}).
   */
  panelForm: FormGroup;
  /** Opções do select de fonte (rótulos i18n; ids = CertificateFontFamily). */
  fontOptions: Array<{ id: string, value: string }> = [];

  /** Pré-visualização com dados de exemplo (ligada) ou {{variáveis}} cruas. */
  previewData = true;
  sampleVars: CertificateVars;

  /** Sugestões do menu "{{" do editor de texto (ids COM chaves — ver HTML). */
  templateVariables: SelectOption[] = [];

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  /** Destino do próximo upload: elemento novo, troca do selecionado ou fundo. */
  private uploadTarget: 'element' | 'replace' | 'background' = 'element';

  // ─── Chrome fixo (layout "app") ────────────────────────────────────────
  // Header (topo), dock (fundo), painel (direita) e palco (centro) são
  // position:fixed; a geometria é sincronizada por frame com a ÁREA DE
  // CONTEÚDO (host da página, à direita do sidebar) — mesma técnica do
  // header fixo da Lista de submissões: um ResizeObserver NÃO dispara em
  // mudança só de posição (troca Moderno↔Clássico, toggle do menu lateral),
  // então observamos a geometria real a cada frame, fora da zona do Angular
  // (só escreve estilo quando algo mudou — zero CD, zero churn).
  @ViewChild('fixedHeader') private fixedHeaderRef: ElementRef<HTMLElement>;
  @ViewChild('fixedStage') private fixedStageRef: ElementRef<HTMLElement>;
  @ViewChild('fixedPanel') private fixedPanelRef: ElementRef<HTMLElement>;
  @ViewChild('fixedDock') private fixedDockRef: ElementRef<HTMLElement>;
  private static readonly PANEL_WIDTH = 300;
  private chromeRafId = 0;
  private lastChromeKey = '';
  /** Âncora da geometria: a ÁREA DE CONTEÚDO INTEIRA (mat-sidenav-content),
   *  não o host — o host vive recuado pelo padding lateral do shell, o que
   *  deixaria uma faixa descoberta entre o sidebar e o palco full-bleed. */
  private canvasEl: HTMLElement | null = null;
  /** Sidebar experimental (shell Moderno) + activity-bar. Quando presentes, a
   *  borda ESQUERDA do chrome ancora na borda direita ANIMADA do sidebar em vez
   *  do `left` da área de conteúdo: a `margin-left` do mat-sidenav-content vem
   *  de uma CSS var não-registrada que PULA (não anima), então `canvasEl.left`
   *  salta pro valor final no 1º frame enquanto o sidebar ainda desliza —
   *  faziam-se "dois painéis". A largura do sidebar (getBoundingClientRect)
   *  anima de verdade (transition: width), então rastreá-la casa o palco com a
   *  animação, quadro a quadro. Layouts sem esses elementos caem no `left` do
   *  conteúdo (comportamento original). */
  private shellSidebarEl: HTMLElement | null = null;
  private shellActivityBarEl: HTMLElement | null = null;
  private shellAnchorsResolved = false;

  private readonly trackChrome = (): void => {
    const header = this.fixedHeaderRef && this.fixedHeaderRef.nativeElement;
    if (header) {
      const host = this.elementRef.nativeElement as HTMLElement;
      if (!this.canvasEl) {
        this.canvasEl = (host.closest('mat-sidenav-content') as HTMLElement) || host;
      }
      const rect = this.canvasEl.getBoundingClientRect();
      // Borda esquerda do chrome: no shell Moderno, a borda direita ANIMADA do
      // sidebar (+ largura da activity-bar) — `rect.left` saltaria de uma vez
      // (var não-animável). Fallback: `rect.left` (layouts sem sidebar exp.).
      if (!this.shellAnchorsResolved) {
        this.shellSidebarEl = document.querySelector('.experimental-sidebar') as HTMLElement | null;
        this.shellActivityBarEl = document.querySelector('.activity-bar') as HTMLElement | null;
        // Só marca resolvido quando o shell já montou; senão tenta no próximo frame.
        if (this.shellSidebarEl) { this.shellAnchorsResolved = true; }
      }
      let leftEdge = rect.left;
      if (this.shellSidebarEl) {
        const sbRight = this.shellSidebarEl.getBoundingClientRect().right;
        // sbRight <= 0 ⇒ sidebar oculto/destacado (ex.: shell Clássico) → mantém
        // o fallback no `left` do conteúdo.
        if (sbRight > 0) {
          const abW = this.shellActivityBarEl ? this.shellActivityBarEl.getBoundingClientRect().width : 0;
          leftEdge = sbRight + abW;
        }
      }
      // Largura útil = da borda animada até a borda DIREITA (estável) do conteúdo.
      const contentRight = rect.right;
      const dock = this.fixedDockRef && this.fixedDockRef.nativeElement;
      const stage = this.fixedStageRef && this.fixedStageRef.nativeElement;
      const panel = this.fixedPanelRef && this.fixedPanelRef.nativeElement;
      const dockH = dock ? dock.offsetHeight : 0;
      // Sem modelo o painel não renderiza → o palco ocupa a largura toda.
      const panelPresent = !!panel;
      // `top` do header vem do CSS (ciente do modo Moderno/Clássico/amplo);
      // o resto deriva dele + do rect do host + da altura real do dock.
      const headerBottom = header.getBoundingClientRect().bottom;
      const dockTop = window.innerHeight - dockH;
      const panelW = CertificateTemplateEditorComponent.PANEL_WIDTH;
      // Presença de dock/painel/palco entra na chave: eles montam DEPOIS do
      // header (gate `!loading`) e a geometria pode não ter mudado nesse meio
      // tempo — sem isso, ficariam sem estilo até o próximo resize.
      // `leftEdge` (não `rect.left`) na chave: durante a animação do sidebar ele
      // muda a cada frame — sem isso o cache congelaria após o salto do canvas.
      const contentWidth = Math.max(0, contentRight - leftEdge);
      const key = `${leftEdge}|${contentWidth}|${headerBottom}|${dockH}|${!!dock}|${!!panel}|${!!stage}`;
      if (key !== this.lastChromeKey) {
        this.lastChromeKey = key;
        header.style.left = `${leftEdge}px`;
        header.style.width = `${contentWidth}px`;
        if (dock) {
          dock.style.left = `${leftEdge}px`;
          dock.style.width = `${contentWidth}px`;
        }
        if (panel) {
          panel.style.left = `${contentRight - panelW}px`;
          panel.style.width = `${panelW}px`;
          panel.style.top = `${headerBottom}px`;
          panel.style.height = `${Math.max(0, dockTop - headerBottom)}px`;
        }
        if (stage) {
          stage.style.left = `${leftEdge}px`;
          stage.style.width = `${Math.max(0, contentWidth - (panelPresent ? panelW : 0))}px`;
          stage.style.top = `${headerBottom}px`;
          stage.style.height = `${Math.max(0, dockTop - headerBottom)}px`;
        }
      }
    }
    this.chromeRafId = requestAnimationFrame(this.trackChrome);
  };

  constructor(
    private adminService: AdminService,
    private certificatesService: CertificatesService,
    private certificatePdf: CertificatePdfService,
    private notification: NotificationService,
    private translate: TranslateService,
    private elementRef: ElementRef,
    private ngZone: NgZone,
    private fb: FormBuilder,
    private eventsService: EventsService,
    private dialog: MatDialog,
  ) {
    this.panelForm = this.fb.group({ font: ['times'] });
    this.panelForm.get('font').valueChanges.subscribe(value => {
      const text = this.selectedText;
      if (text && value && text.font !== value) {
        text.font = value;
        this.markDirty();
      }
    });
  }

  /** Espelha o elemento selecionado nos controles reactive (sem re-emitir). */
  private syncPanelForm(): void {
    const text = this.selectedText;
    if (text && this.panelForm) {
      this.panelForm.get('font').setValue(text.font, { emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.chromeRafId = requestAnimationFrame(this.trackChrome);
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.chromeRafId);
  }

  ngOnInit(): void {
    const boot = (event: Event) => {
      this.event = event;
      this.buildSampleVars();
      this.load();
    };
    if (this.adminService.selectedEvent) {
      boot(this.adminService.selectedEvent);
    } else {
      this.adminService.getEvent().subscribe(boot);
    }
  }

  hasUnsavedChanges(): boolean { return this.dirty; }

  private buildSampleVars(): void {
    this.templateVariables = CERTIFICATE_VAR_NAMES.map(name => new SelectOption(
      '{{' + name + '}}',
      this.translate.instant('admin.event.certificate.vars.' + name)));
    this.fontOptions = ['times', 'helvetica', 'courier'].map(id => ({
      id,
      value: this.translate.instant('admin.event.certificate.font-options.' + id),
    }));
    const sample = (key: string) => this.translate.instant('admin.event.certificate.' + key);
    const today = new Date().toLocaleDateString(
      this.translate.currentLang === 'pt-BR' ? 'pt-BR' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' });
    // Datas/local do evento: usa o eventInfo REAL quando hidratado; senão amostra.
    const info = this.event && this.event.eventInfo;
    const fmt = (d: any) => d ? new Date(d.toDate ? d.toDate() : d).toLocaleDateString(
      this.translate.currentLang === 'pt-BR' ? 'pt-BR' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' }) : '';
    const begin = info && fmt(info.beginAt);
    const end = info && fmt(info.endAt);
    this.sampleVars = {
      event: (this.event && this.event.name) || '',
      eventAcronym: (this.event && this.event.acronym) || '',
      eventYear: this.event && this.event.year ? String(this.event.year) : '',
      eventDates: begin && end && begin !== end ? `${begin} – ${end}` : (begin || end || sample('sample-event-dates')),
      eventLocation: info && (info.city || info.country)
        ? [info.city, info.country].filter(Boolean).join(', ') : sample('sample-event-location'),
      title: sample('sample-title'),
      id: '245678',
      track: sample('sample-track'),
      topics: sample('sample-topics'),
      submissionGroup: sample('sample-submission-group'),
      tpcGroup: sample('sample-tpc-group'),
      authors: sample('sample-authors'),
      authorsNames: sample('sample-authors-names'),
      firstAuthor: sample('sample-first-author'),
      chairs: sample('sample-chairs'),
      session: sample('sample-session'),
      sessionDate: today,
      room: sample('sample-room'),
      submittedAt: today,
      date: today,
    };
  }

  get varsForSheet(): CertificateVars | null {
    return this.previewData ? this.sampleVars : null;
  }

  private load(): void {
    this.loading = true;
    this.certificatesService.getEventCertificate(this.event.id).subscribe(data => {
      this.images = data.images;
      // SEM modelo → template null: o palco mostra o empty-state "Criar
      // modelo" (a folha só existe quando há modelo — real ou em edição).
      this.template = data.template;
      this.dormantEmission = data.dormantEmission;
      this.selectedId = null;
      this.dirty = false;
      this.loading = false;
    }, () => {
      this.template = null;
      this.loading = false;
    });
  }

  /** Empty-state → modelo novo com FOLHA VAZIA. Regras: as DORMENTES do
   *  modelo excluído, se existirem; senão o default ("Aceita"). */
  createTemplate(): void {
    const t = emptyCertificateTemplate();
    if (this.dormantEmission) {
      t.emission = { allowedStatuses: [...this.dormantEmission.allowedStatuses] };
    }
    this.template = t;
    this.selectedId = null;
    this.markDirty();
  }

  // ------------------------------------------------------------------ seleção

  get selected(): CertificateElement | null {
    return (this.template && this.template.elements.find(e => e.id === this.selectedId)) || null;
  }
  get selectedText(): CertificateTextElement | null {
    const el = this.selected;
    return el && isTextElement(el) ? el : null;
  }
  get selectedImage(): CertificateImageElement | null {
    const el = this.selected;
    return el && isImageElement(el) ? el : null;
  }

  markDirty(): void { this.dirty = true; }

  // ----------------------------------------------------------------- elementos

  addTextBox(): void {
    const el: CertificateTextElement = {
      id: newCertificateElementId(),
      type: 'text',
      x: Math.round((this.sheetW - 500) / 2),
      y: 120 + (this.template.elements.length % 5) * 40,   // cascata leve
      w: 500,
      align: 'center',
      font: 'times',
      fontSize: 24,
      lineHeight: 1.5,
      bold: false,
      italic: false,
      content: this.translate.instant('admin.event.certificate.new-text-content'),
    };
    this.template.elements.push(el);
    this.selectedId = el.id;
    this.markDirty();
  }

  addImageElement(img: CertificateImage): void {
    const el: CertificateImageElement = {
      id: newCertificateElementId(),
      type: 'image',
      x: Math.round((this.sheetW - 300) / 2),
      y: Math.round((this.sheetH - 180) / 2),
      w: 300,
      h: 180,
      imageId: img.id,
      url: img.url,
      crop: { zoom: 1, offsetX: 0, offsetY: 0 },
    };
    this.template.elements.push(el);
    this.selectedId = el.id;
    this.markDirty();
  }

  removeSelected(): void {
    if (!this.selected) { return; }
    this.template.elements = this.template.elements.filter(e => e.id !== this.selectedId);
    this.selectedId = null;
    this.markDirty();
  }

  replaceSelectedImage(img: CertificateImage): void {
    const el = this.selectedImage;
    if (!el) { return; }
    el.imageId = img.id;
    el.url = img.url;
    el.crop = { zoom: 1, offsetX: 0, offsetY: 0 };
    this.markDirty();
  }

  resetCrop(): void {
    const el = this.selectedImage;
    if (!el) { return; }
    el.crop = { zoom: 1, offsetX: 0, offsetY: 0 };
    this.markDirty();
  }

  // -------------------------------------------------------------------- fundo

  setBackground(img: CertificateImage): void {
    this.template.background = {
      imageId: img.id,
      url: img.url,
      opacity: this.template.background ? this.template.background.opacity : 1,
    };
    this.markDirty();
  }

  removeBackground(): void {
    this.template.background = null;
    this.markDirty();
  }

  // ------------------------------------------------------------------ imagens

  /** Abre o seletor de arquivo; o destino define o que fazer no upload. */
  pickImage(target: 'element' | 'replace' | 'background'): void {
    this.uploadTarget = target;
    if (this.fileInput) { this.fileInput.nativeElement.click(); }
  }

  onFileChosen(input: HTMLInputElement): void {
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) { return; }
    this.certificatesService.uploadImage(this.event.id, file).subscribe(img => {
      this.images = [...this.images, img];
      if (this.uploadTarget === 'background') {
        this.setBackground(img);
      } else if (this.uploadTarget === 'replace') {
        this.replaceSelectedImage(img);
      } else {
        this.addImageElement(img);
      }
    }, () => this.notification.notifyError('admin.event.certificate.upload-error'));
  }

  /** Remove da biblioteca; bloqueia se a imagem estiver em uso no template. */
  deleteImage(img: CertificateImage): void {
    const inUse = (this.template.background && this.template.background.imageId === img.id)
      || this.template.elements.some(e => isImageElement(e) && e.imageId === img.id);
    if (inUse) {
      this.notification.notifyError('admin.event.certificate.image-in-use');
      return;
    }
    this.certificatesService.deleteImage(this.event.id, img.id).subscribe(() => {
      this.images = this.images.filter(i => i.id !== img.id);
    }, () => this.notification.notifyError('errors.catastrophe'));
  }

  // -------------------------------------------------- copiar de outro evento

  @ViewChild('copyEventDialog') copyEventDialogTpl: TemplateRef<unknown>;
  private copyDialogRef: MatDialogRef<unknown> | null = null;

  // Lista filtrável de eventos (mesmo padrão do "importar tópicos").
  availableEvents: Array<{ id: number, label: string }> = [];
  eventFilter = '';
  selectedCopyEventId: number | null = null;
  loadingEvents = false;
  loadingCopyPreview = false;
  /** Template do evento-fonte, com urls das imagens DELE (preview ao vivo). */
  copyPreviewTemplate: CertificateTemplate | null = null;
  private copySourceImages: CertificateImage[] = [];
  copying = false;

  openCopyDialog(): void {
    this.loadAvailableEvents();
    this.copyDialogRef = this.dialog.open(this.copyEventDialogTpl, {
      width: '640px',
      maxWidth: '92vw',
      autoFocus: false,
      panelClass: 'copy-certificate-dialog-panel',
    });
    this.copyDialogRef.afterClosed().subscribe(() => {
      this.selectedCopyEventId = null;
      this.copyPreviewTemplate = null;
      this.copySourceImages = [];
      this.eventFilter = '';
      this.copyDialogRef = null;
    });
  }

  private loadAvailableEvents(): void {
    this.loadingEvents = true;
    // Só eventos que TÊM modelo salvo entram na lista (índice do backend) —
    // listar eventos vazios só levaria a cliques em "sem modelo".
    forkJoin([
      this.eventsService.getAllEventsWithMinimumDetails(),
      this.certificatesService.getEventsWithTemplate(),
    ]).subscribe(([events, idsWithTemplate]) => {
      const withTemplate = new Set(idsWithTemplate);
      this.availableEvents = events
        .filter(e => e.id !== (this.event && this.event.id) && withTemplate.has(e.id))
        .map(e => ({ id: e.id, label: `${e.acronym} ${e.year} - ${e.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
      this.loadingEvents = false;
    }, () => { this.loadingEvents = false; });
  }

  filteredAvailableEvents(): Array<{ id: number, label: string }> {
    const q = this.eventFilter.trim().toLocaleLowerCase();
    if (!q) { return this.availableEvents; }
    return this.availableEvents.filter(e => e.label.toLocaleLowerCase().includes(q));
  }

  selectCopyEvent(eventId: number): void {
    this.selectedCopyEventId = eventId;
    this.loadingCopyPreview = true;
    this.copyPreviewTemplate = null;
    this.certificatesService.getEventCertificate(eventId).subscribe(data => {
      this.copyPreviewTemplate = data.template;   // urls já apontam pro evento-fonte
      this.copySourceImages = data.images;
      this.loadingCopyPreview = false;
    }, () => { this.loadingCopyPreview = false; });
  }

  /**
   * Copia o modelo do evento-fonte: baixa as imagens usadas (GET público) e
   * entrega à MESMA cauda da importação (re-upload aqui + remapeamento de ids).
   * Aplica no editor como mudança NÃO SALVA — o chair revisa e salva.
   */
  async confirmCopy(): Promise<void> {
    const sourceId = this.selectedCopyEventId;
    const template = this.copyPreviewTemplate;
    if (!sourceId || !template || this.copying) { return; }
    this.copying = true;

    const usedIds = new Set<number>();
    if (template.background) { usedIds.add(template.background.imageId); }
    template.elements.forEach(e => { if (isImageElement(e)) { usedIds.add(e.imageId); } });

    const files: { [oldId: number]: File } = {};
    for (const id of Array.from(usedIds)) {
      const url = this.certificatesService.imageUrl(sourceId, id);
      const blob = await fetch(url).then(r => r.ok ? r.blob() : null).catch(() => null);
      if (!blob) { continue; }
      const source = this.copySourceImages.find(i => i.id === id);
      files[id] = new File([blob], (source && source.name) || `imagem-${id}`, { type: blob.type || 'image/png' });
    }

    await this.finalizeImport(template, files);
    this.copying = false;
    if (this.copyDialogRef) { this.copyDialogRef.close(); }
    // SEM toast (protocolo do editor: silêncio = sucesso) — o diálogo fecha,
    // a folha copiada aparece e a barra salvar/descartar acende.
  }

  // ------------------------------------------------------------ salvar/restaurar

  save(): void {
    if (!this.template) { return; }
    this.saving = true;
    this.certificatesService.saveTemplate(this.event.id, this.template).subscribe(() => {
      this.saving = false;
      // SEM toast: a barra desabilitar (dirty=false) no ponto do clique JÁ é
      // a confirmação. Protocolo do editor: silêncio = sucesso; toast = erro.
      this.dirty = false;
    }, () => {
      this.saving = false;
      this.notification.notifyError('errors.catastrophe');
    });
  }

  discard(): void { this.load(); }

  /** LIMPA a folha no editor (volta ao vazio; persiste só quando salvar). */
  clearSheet(): void {
    this.template = emptyCertificateTemplate();
    this.selectedId = null;
    this.markDirty();
  }

  /**
   * EXCLUI o modelo do evento no servidor (≠ "Limpar folha", que só
   * reseta o desenho local): o evento volta ao estado "sem modelo" e os
   * certificados usam o padrão do JEMS. Imagens da biblioteca permanecem.
   * Destrutivo e imediato → confirmação antes.
   */
  deleteTemplate(): void {
    // Deferir 1 tick: o mat-menu ainda está fechando (backdrop vivo +
    // restauração de foco pro gatilho) — abrir o modal JUNTO fazia o menu
    // roubar o foco de volta e engolir o primeiro clique no modal.
    setTimeout(() => this.openDeleteConfirmation());
  }

  private openDeleteConfirmation(): void {
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.certificate.delete-confirm-title',
        content: 'admin.event.certificate.delete-confirm-content',
        cancel: 'forms.buttons.cancel',
        // "Excluir" (chave própria) — o global forms.buttons.delete é "Deletar".
        confirm: 'admin.event.certificate.delete-template',
        destructive: true,
      },
    });
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed !== true) { return; }
      this.saving = true;
      // Exclui o DESENHO mas preserva as regras de emissão dormentes — um
      // modelo futuro criado do zero as reencontra.
      const emission = (this.template && this.template.emission) || null;
      this.certificatesService.deleteTemplateKeepRules(this.event.id, emission).subscribe(() => {
        this.saving = false;
        // SEM toast de sucesso: o editor recarregar no empty-state (os 3
        // botões de criação) JÁ comunica a exclusão — toast seria ruído.
        this.load();
      }, () => {
        this.saving = false;
        this.notification.notifyError('errors.catastrophe');
      });
    });
  }

  /** Baixa um PDF de teste com os dados de exemplo. */
  async downloadPreviewPdf(): Promise<void> {
    const bytes = await this.certificatePdf.generateFromTemplate(this.template, this.sampleVars);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'certificado-modelo.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // ----------------------------------------------------------- exportar/importar

  /**
   * Exporta o template como .zip COMPACTO: `template.json` (layout + manifesto)
   * + imagens como BINÁRIOS CRUS em `images/` — sem base64 (+33% que inflava o
   * .json antigo); o JSON ainda leva DEFLATE. Abre em qualquer ferramenta de
   * zip e pode ser importado em outro evento/instalação do JEMS3.
   */
  async exportTemplate(): Promise<void> {
    const usedIds = new Set<number>();
    if (this.template.background) { usedIds.add(this.template.background.imageId); }
    this.template.elements.forEach(e => { if (isImageElement(e)) { usedIds.add(e.imageId); } });

    const zip = new JSZip();
    const manifest: { [id: number]: { name: string, file: string } } = {};
    for (const id of Array.from(usedIds)) {
      const img = this.images.find(i => i.id === id);
      if (!img) { continue; }
      const blob = await fetch(img.url).then(r => r.ok ? r.blob() : null).catch(() => null);
      if (!blob) { continue; }
      const ext = blob.type.includes('svg') ? 'svg' : blob.type === 'image/jpeg' ? 'jpg' : 'png';
      const path = `images/${id}.${ext}`;
      // PNG/JPEG já são comprimidos → STORE; SVG é texto → DEFLATE rende.
      zip.file(path, blob, { compression: ext === 'svg' ? 'DEFLATE' : 'STORE' });
      manifest[id] = { name: img.name, file: path };
    }
    zip.file('template.json', JSON.stringify({
      jems3CertificateTemplate: 2,
      template: serializeCertificateTemplate(this.template),
      images: manifest,
    }, null, 2), { compression: 'DEFLATE' });

    const out: Blob = await zip.generateAsync({ type: 'blob' });
    this.downloadBlob(out, 'modelo-certificado.zip');
  }

  onImportFileChosen(input: HTMLInputElement): void {
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) { return; }
    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      const head = new Uint8Array(buffer.slice(0, 2));
      // 'PK' = zip (formato v2); senão, tenta o .json legado (v1, base64).
      if (head[0] === 0x50 && head[1] === 0x4B) {
        this.importFromZip(buffer);
      } else {
        this.importFromLegacyJson(new TextDecoder('utf-8').decode(buffer));
      }
    };
    reader.readAsArrayBuffer(file);
  }

  /** Formato v2: .zip com template.json + images/<id>.<ext> binários. */
  private async importFromZip(buffer: ArrayBuffer): Promise<void> {
    let template: ReturnType<typeof sanitizeCertificateTemplate> = null;
    const files: { [oldId: number]: File } = {};
    try {
      const zip = await JSZip.loadAsync(buffer);
      const entry = zip.file('template.json');
      const payload = entry ? JSON.parse(await entry.async('string')) : null;
      if (payload && payload.jems3CertificateTemplate === 2) {
        template = sanitizeCertificateTemplate(payload.template);
        const manifest = (payload.images && typeof payload.images === 'object') ? payload.images : {};
        for (const oldId of Object.keys(manifest)) {
          const item = manifest[oldId];
          const imgEntry = item && item.file ? zip.file(String(item.file)) : null;
          if (!imgEntry) { continue; }
          const bytes: Uint8Array = await imgEntry.async('uint8array');
          const file = String(item.file);
          const mime = file.endsWith('.svg') ? 'image/svg+xml'
            : file.endsWith('.jpg') ? 'image/jpeg' : 'image/png';
          files[Number(oldId)] = new File([bytes], (item.name || `imagem-${oldId}`), { type: mime });
        }
      }
    } catch { template = null; }
    await this.finalizeImport(template, files);
  }

  /** Formato v1 (legado): .json com imagens embutidas em base64. */
  private async importFromLegacyJson(json: string): Promise<void> {
    let payload: any;
    try { payload = JSON.parse(json); } catch { payload = null; }
    const template = payload && payload.jems3CertificateTemplate === 1
      ? sanitizeCertificateTemplate(payload.template) : null;
    const files: { [oldId: number]: File } = {};
    const embedded = (payload && payload.images && typeof payload.images === 'object') ? payload.images : {};
    for (const oldId of Object.keys(embedded)) {
      const entry = embedded[oldId];
      const file = this.dataUrlToFile(entry && entry.dataUrl, (entry && entry.name) || 'imagem.png');
      if (file) { files[Number(oldId)] = file; }
    }
    await this.finalizeImport(template, files);
  }

  /**
   * Cauda comum da importação: re-sobe as imagens NESTE evento, remapeia os
   * ids (os do arquivo pertencem ao evento de origem — só sobrevive o que
   * foi re-subido; fundo órfão → null, elemento órfão → some) e aplica.
   */
  private async finalizeImport(
    template: CertificateTemplate | null,
    filesByOldId: { [oldId: number]: File },
  ): Promise<void> {
    if (!template) {
      this.notification.notifyError('admin.event.certificate.import-invalid');
      return;
    }

    const idMap: { [oldId: number]: CertificateImage } = {};
    for (const oldId of Object.keys(filesByOldId)) {
      try {
        const img = await this.certificatesService.uploadImage(this.event.id, filesByOldId[oldId]).toPromise();
        this.images = [...this.images, img];
        idMap[Number(oldId)] = img;
      } catch { /* imagem inválida — elemento ficará órfão e será descartado */ }
    }

    if (template.background) {
      const mapped = idMap[template.background.imageId];
      if (mapped) { template.background.imageId = mapped.id; } else { template.background = null; }
    }
    template.elements = template.elements.filter(el => {
      if (!isImageElement(el)) { return true; }
      const mapped = idMap[el.imageId];
      if (!mapped) { return false; }
      el.imageId = mapped.id;
      return true;
    });

    this.certificatesService.attachImageUrls(template, this.event.id);
    this.template = template;
    this.selectedId = null;
    this.markDirty();
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private dataUrlToFile(dataUrl: string, name: string): File | null {
    if (!dataUrl || !dataUrl.startsWith('data:')) { return null; }
    try {
      const [head, body] = dataUrl.split(',');
      const mime = (head.match(/data:([^;]+)/) || [])[1] || 'image/png';
      if (mime !== 'image/png' && mime !== 'image/jpeg' && mime !== 'image/svg+xml') { return null; }
      const bytes = atob(body);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) { arr[i] = bytes.charCodeAt(i); }
      return new File([arr], name, { type: mime });
    } catch {
      return null;
    }
  }
}
